/**
 * Le module Finance — lot 1 : ce que chaque société paramètre.
 *
 * Cadrage « Stafa v2 — Étape 9 : le coût de revient », clos le 06/09/2026.
 * Maquette `docs/maquettes/cout-de-revient.html`.
 *
 * Même découpe que partout : `VueFinance` dessine, `EcranFinance` décide, et le
 * calcul est dans `finance.ts`, en fonctions pures.
 *
 *
 * CET ÉCRAN NE CALCULE AUCUN COÛT DE TRANSPORT
 * ---------------------------------------------------------------------------
 * Il montre ce qu'une ressource coûte par JOUR et par HEURE — deux divisions
 * qui se lisent de la seule saisie, et qui donnent à voir l'effet de ce qu'on
 * tape. L'imputation à un ordre est le lot 2 ; le coût du mois et les jours
 * creux sont le lot 3.
 *
 *
 * IL MONTRE CE QUI MANQUE
 * ---------------------------------------------------------------------------
 * Trois trous se disent, au lieu d'être comblés :
 *
 *   - **une ressource sans prix** : le décompte en tête compte combien, et la
 *     ligne écrit « à saisir » plutôt qu'un tiret ;
 *   - **l'amplitude non réglée** : aucun coût horaire ne s'affiche, et un
 *     bandeau dit pourquoi. Poser dix heures d'office ferait un chiffre faux
 *     que rien ne signalerait ;
 *   - **l'indice gazole d'un autre mois** : il se dit périmé. C'est une tâche
 *     mensuelle de l'éditeur, et sans ce signal chaque société calculerait avec
 *     le prix d'un autre mois sans le savoir.
 *
 * C'est la même règle que le remplissage d'une tournée, qui se tait quand les
 * dimensions sont inconnues.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  changerLaNature,
  creerUnContrat,
  creerUnFrais,
  fermerUnFrais,
  lireContrats,
  lireFrais,
  lireGazole,
  lireListe,
  lireParc,
  poserUnCout,
  reglerLaFinance,
  supprimerUnContrat,
  supprimerUnCout,
  supprimerUnFrais,
  type ContratTraction,
  type EchecApi,
  type FraisFinance,
  type IndiceGazole,
  type LigneListe,
  type ReglagesFinance,
  type RessourceFinance,
  type UniteFrais,
} from './api.js';
import { lireRessourcesFinance } from './api.js';
import { ANNUAIRE_TRANSPORTEURS } from './transporteurs.js';
import { PARC_CHAUFFEURS, PARC_VEHICULES } from './parcs.js';
import {
  calendrierDit,
  corpsCout,
  coutHoraire,
  coutJournalier,
  decimalesDe,
  decompteRessources,
  euros,
  gazoleDit,
  moisCourant,
  moisDecale,
  moisDit,
  refusCout,
  SEMAINE,
  sorteDite,
  uniteDite,
  type CleJour,
  type SaisieCout,
} from './finance.js';

export type OngletFinance = 'ressources' | 'frais' | 'traction';

/** Ce que la boîte de contrat a besoin de savoir d'un camion ou d'un chauffeur. */
export interface LigneRessource {
  readonly id: string;
  readonly nom: string;
}

export type EtatFinance =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | {
      readonly nom: 'prete';
      readonly mois: string;
      readonly ressources: readonly RessourceFinance[];
      readonly reglages: ReglagesFinance;
    };

function Absent() {
  return <span className="absent">—</span>;
}

/** « à saisir » plutôt qu'un tiret : ce n'est pas absent, c'est à faire. */
function ASaisir() {
  return <span className="attente">à saisir</span>;
}

const OUVRES: Record<CleJour, boolean> = {
  lundi: true,
  mardi: true,
  mercredi: true,
  jeudi: true,
  vendredi: true,
  samedi: false,
  dimanche: false,
};

// ---------------------------------------------------------------------------
// La boîte du coût
// ---------------------------------------------------------------------------

/**
 * LE PRIX MENSUEL ET LE CALENDRIER.
 *
 * La boîte montre EN DIRECT le coût journalier que la saisie donnera, comme le
 * panneau d'affrètement montre la marge pendant qu'on tape le prix d'achat. On
 * voit alors ce que cocher le samedi change.
 */
export function BoiteCout({
  saisie,
  joursDuMois,
  amplitude,
  envoi,
  refus,
  quandChamp,
  quandJour,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: SaisieCout;
  /** Combien de jours du mois regardé tomberaient sur les cases cochées. */
  readonly joursDuMois: number;
  readonly amplitude: string | null;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: 'prixMensuel' | 'consommation' | 'du', v: string) => void;
  readonly quandJour: (jour: CleJour) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  const journalier = coutJournalier(
    saisie.prixMensuel.trim().replace(',', '.'),
    joursDuMois,
  );
  const horaire = coutHoraire(journalier, amplitude);
  const faute = refusCout(saisie);

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Coût de {saisie.nom}</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="cout-prix">
            Prix mensuel HT
          </label>
          <input
            id="cout-prix"
            className="champ-boite"
            type="text"
            inputMode="decimal"
            value={saisie.prixMensuel}
            disabled={envoi}
            onChange={(e) => quandChamp('prixMensuel', e.target.value)}
          />

          <span className="etiquette-boite">Jours exploités</span>
          {/* SEPT CASES ET NON « 26 jours par mois » : elles disent QUELS
              jours, ce qui permet de compter exactement dans un mois donné —
              un mois a cinq samedis, un autre quatre. */}
          <div className="semaine-cases">
            {SEMAINE.map((j) => (
              <label key={j.cle} className="case-jour" title={j.nom}>
                <input
                  type="checkbox"
                  checked={saisie.jours[j.cle]}
                  disabled={envoi}
                  onChange={() => quandJour(j.cle)}
                />
                <span>{j.lettre}</span>
              </label>
            ))}
          </div>

          {/* L'EFFET DE CE QU'ON TAPE, en direct. Le samedi coché fait baisser
              le coût journalier sous les yeux. */}
          {journalier !== null && (
            <p className="calcule">
              {joursDuMois} jours en {moisDit(saisie.du.slice(0, 7))} —{' '}
              {euros(journalier) ?? '—'} la journée
              {horaire !== null && `, ${euros(horaire) ?? '—'} l’heure`}
            </p>
          )}
          {journalier !== null && horaire === null && (
            <p className="aide-boite">
              Le coût horaire demande l’amplitude de travail, qui n’est pas
              réglée. Elle se pose en haut de l’onglet Ressources.
            </p>
          )}

          {saisie.sorte === 'vehicule' && (
            <>
              <label className="etiquette-boite" htmlFor="cout-conso">
                Consommation, en litres aux 100 km
              </label>
              <input
                id="cout-conso"
                className="champ-boite"
                type="text"
                inputMode="decimal"
                value={saisie.consommation}
                disabled={envoi}
                onChange={(e) => quandChamp('consommation', e.target.value)}
              />
            </>
          )}

          <label className="etiquette-boite" htmlFor="cout-du">
            À partir du
          </label>
          <input
            id="cout-du"
            className="champ-boite"
            type="date"
            value={saisie.du}
            disabled={envoi}
            onChange={(e) => quandChamp('du', e.target.value)}
          />
          <p className="aide-boite">
            Le prix en cours sera fermé la veille. Il ne disparaît pas : les mois
            déjà passés gardent le leur, sans quoi corriger un loyer aujourd’hui
            réécrirait la marge de juillet.
          </p>

          {(refus ?? faute) !== null && (
            <p className="bandeau-refus">{refus ?? faute}</p>
          )}

          <div className="actions">
            <button
              className="bouton"
              type="submit"
              disabled={envoi || faute !== null}
            >
              {envoi ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// La vue
// ---------------------------------------------------------------------------

export interface ProprietesVueFinance {
  readonly onglet: OngletFinance;
  readonly etat: EtatFinance;
  readonly mois: string;
  readonly frais: readonly FraisFinance[];
  readonly contrats: readonly ContratTraction[];
  readonly gazole: readonly IndiceGazole[];
  readonly transporteurs: readonly LigneListe[];
  /**
   * LE PARC, réduit à ce que la boîte de contrat affiche. `lireParc` rend des
   * lignes ouvertes — chaque parc a ses colonnes — et n'en garder que deux
   * évite de dépendre ici de la forme d'un camion.
   */
  readonly vehicules: readonly LigneRessource[];
  readonly chauffeurs: readonly LigneRessource[];
  readonly cout: SaisieCout | null;
  readonly amplitudeSaisie: string;
  readonly nouveauFrais: {
    readonly libelle: string;
    readonly montant: string;
    readonly unite: UniteFrais;
    readonly ressource: string;
  } | null;
  readonly nouveauContrat: {
    readonly transporteurId: string;
    readonly vehiculeId: string;
    readonly chauffeurId: string;
    readonly mode: 'jour' | 'km' | 'voyage';
    readonly prix: string;
  } | null;
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandOnglet: (onglet: OngletFinance) => void;
  readonly quandMois: (mois: string) => void;
  readonly quandAmplitude: (valeur: string) => void;
  readonly quandEnregistrerAmplitude: () => void;
  readonly quandNature: (r: RessourceFinance) => void;
  readonly quandCout: (r: RessourceFinance) => void;
  readonly quandChampCout: (
    cle: 'prixMensuel' | 'consommation' | 'du',
    v: string,
  ) => void;
  readonly quandJourCout: (jour: CleJour) => void;
  readonly quandEnregistrerCout: () => void;
  readonly quandSupprimerCout: (id: string) => void;
  readonly quandFermerBoite: () => void;
  readonly quandOuvrirFrais: () => void;
  readonly quandChampFrais: (cle: string, v: string) => void;
  readonly quandEnregistrerFrais: () => void;
  readonly quandFermerFrais: (id: string) => void;
  readonly quandSupprimerFrais: (id: string) => void;
  readonly quandOuvrirContrat: () => void;
  readonly quandChampContrat: (cle: string, v: string) => void;
  readonly quandEnregistrerContrat: () => void;
  readonly quandSupprimerContrat: (id: string) => void;
}

export function VueFinance(p: ProprietesVueFinance) {
  const prete = p.etat.nom === 'prete' ? p.etat : null;
  const amplitude = prete?.reglages.amplitude_heures ?? null;
  const gaz = gazoleDit(p.gazole, p.mois);

  return (
    <div className="corps corps-ot">
      <section className="liste">
        {p.cout !== null && (
          <BoiteCout
            saisie={p.cout}
            joursDuMois={joursDansLeMois(p.mois, p.cout.jours)}
            amplitude={amplitude}
            envoi={p.envoi}
            refus={p.refus}
            quandChamp={p.quandChampCout}
            quandJour={p.quandJourCout}
            quandFermer={p.quandFermerBoite}
            quandEnregistrer={p.quandEnregistrerCout}
          />
        )}

        <div className="onglets-listes">
          {(
            [
              ['ressources', 'Ressources'],
              ['frais', 'Frais'],
              ['traction', 'Traction'],
            ] as const
          ).map(([cle, nom]) => (
            <button
              key={cle}
              type="button"
              className={p.onglet === cle ? 'onglet-liste actif' : 'onglet-liste'}
              onClick={() => {
                p.quandOnglet(cle);
              }}
            >
              {nom}
            </button>
          ))}
        </div>

        {p.onglet === 'ressources' && (
          <>
            <div className="barre-outils">
              <button
                className="bouton bouton-discret"
                type="button"
                onClick={() => {
                  p.quandMois(moisDecale(p.mois, -1));
                }}
              >
                ←
              </button>
              <span className="etiquette-mois">{moisDit(p.mois)}</span>
              <button
                className="bouton bouton-discret"
                type="button"
                onClick={() => {
                  p.quandMois(moisDecale(p.mois, 1));
                }}
              >
                →
              </button>

              <label className="champ-date">
                <span className="etiquette">Amplitude, en heures</span>
                <input
                  className="champ court"
                  type="text"
                  inputMode="decimal"
                  placeholder="non réglée"
                  value={p.amplitudeSaisie}
                  onChange={(e) => {
                    p.quandAmplitude(e.target.value);
                  }}
                  onBlur={p.quandEnregistrerAmplitude}
                />
              </label>
            </div>

            {p.etat.nom === 'chargement' && <p className="attente">Chargement…</p>}
            {p.etat.nom === 'panne' && (
              <p className="bandeau-refus">Les ressources n’ont pas pu être lues.</p>
            )}

            {prete !== null && (
              <>
                <p className="decompte">{decompteRessources(prete.ressources)}</p>

                {/* L'INDICE GAZOLE, ET S'IL DATE. Tâche mensuelle de
                    l'éditeur : sans ce signal, on calculerait avec le prix d'un
                    autre mois sans le savoir. */}
                <p className={gaz.perime ? 'aide-saisie attente' : 'aide-saisie'}>
                  {gaz.texte}
                </p>

                {amplitude === null && (
                  <p className="aide-saisie attente">
                    L’amplitude de travail n’est pas réglée : aucun coût horaire
                    ne se calcule. Elle ne se devine pas — dix heures posées
                    d’office donneraient un chiffre faux que rien ne signalerait.
                  </p>
                )}

                <div className="tableau-cadre">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Sorte</th>
                        <th scope="col" className="col-souple">
                          Ressource
                        </th>
                        <th scope="col">Nature</th>
                        <th scope="col" className="prix">
                          Prix mensuel
                        </th>
                        <th scope="col" className="col-large col-souple">
                          Jours exploités
                        </th>
                        <th scope="col">Jours</th>
                        <th scope="col" className="prix">
                          La journée
                        </th>
                        <th scope="col" className="prix col-large">
                          L’heure
                        </th>
                        <th scope="col" className="col-large">
                          Conso.
                        </th>
                        <th scope="col" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {prete.ressources.map((r) => {
                        const journalier = coutJournalier(
                          r.prix_mensuel,
                          r.jours_exploites,
                        );
                        return (
                          <tr key={`${r.sorte}-${r.id}`} className={r.actif ? undefined : 'eteinte'}>
                            <td className="sourdine">{sorteDite(r.sorte)}</td>
                            <td className="col-souple">
                              {r.nom}
                              {r.detail !== null && (
                                <span className="secondaire">{r.detail}</span>
                              )}
                            </td>
                            <td>
                              <button
                                className="lien-action"
                                type="button"
                                disabled={p.envoi}
                                onClick={() => {
                                  p.quandNature(r);
                                }}
                              >
                                {r.nature === 'propre' ? 'En propre' : 'Tractionnaire'}
                              </button>
                            </td>
                            <td className="prix">
                              {r.prix_mensuel === null ? (
                                <ASaisir />
                              ) : (
                                (euros(Number(r.prix_mensuel)) ?? <Absent />)
                              )}
                            </td>
                            <td className="col-large col-souple sourdine">
                              {calendrierDit(r) ?? <Absent />}
                            </td>
                            <td className="code">
                              {r.prix_mensuel === null ? (
                                <Absent />
                              ) : (
                                (r.jours_exploites ?? 0)
                              )}
                            </td>
                            <td className="prix">
                              {euros(journalier) ?? <Absent />}
                            </td>
                            <td className="prix col-large">
                              {euros(coutHoraire(journalier, amplitude)) ?? <Absent />}
                            </td>
                            <td className="code col-large">
                              {r.sorte !== 'vehicule' ? (
                                <span className="sourdine">—</span>
                              ) : r.consommation === null ? (
                                <ASaisir />
                              ) : (
                                `${r.consommation} L`
                              )}
                            </td>
                            <td className="actions-liste">
                              <button
                                className="lien-action"
                                type="button"
                                disabled={p.envoi}
                                onClick={() => {
                                  p.quandCout(r);
                                }}
                              >
                                {r.prix_mensuel === null ? 'Saisir' : 'Corriger'}
                              </button>
                              {r.cout_id !== null && (
                                <button
                                  className="lien-action risque"
                                  type="button"
                                  disabled={p.envoi}
                                  onClick={() => {
                                    p.quandSupprimerCout(r.cout_id as string);
                                  }}
                                >
                                  Retirer
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {p.onglet === 'frais' && (
          <>
            <div className="barre-outils">
              <button className="bouton" type="button" onClick={p.quandOuvrirFrais}>
                Ajouter un frais
              </button>
            </div>
            <p className="aide-saisie">
              Chaque frais porte son UNITÉ. Sans elle, des pneumatiques — qui
              s’usent au kilomètre — seraient inscrits comme une charge
              mensuelle, et effaceraient tout écart entre un camion qui fait
              200 km par jour et un qui en fait 800.
            </p>

            {p.nouveauFrais !== null && (
              <BoiteFrais
                saisie={p.nouveauFrais}
                ressources={prete?.ressources ?? []}
                envoi={p.envoi}
                refus={p.refus}
                quandChamp={p.quandChampFrais}
                quandFermer={p.quandFermerBoite}
                quandEnregistrer={p.quandEnregistrerFrais}
              />
            )}

            {p.frais.length === 0 ? (
              <div className="vide">
                <p>Aucun frais saisi.</p>
              </div>
            ) : (
              <div className="tableau-cadre">
                <table>
                  <thead>
                    <tr>
                      <th scope="col" className="col-souple">
                        Libellé
                      </th>
                      <th scope="col" className="prix">
                        Montant
                      </th>
                      <th scope="col">Unité</th>
                      <th scope="col" className="col-souple">
                        Sur quoi
                      </th>
                      <th scope="col">Depuis</th>
                      <th scope="col">Jusqu’au</th>
                      <th scope="col" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {p.frais.map((f) => (
                      <tr key={f.id} className={f.au === null ? undefined : 'eteinte'}>
                        <td className="col-souple">{f.libelle}</td>
                        <td className="prix">
                          {euros(Number(f.montant), decimalesDe(f.unite)) ?? <Absent />}
                        </td>
                        <td className="sourdine">{uniteDite(f.unite)}</td>
                        <td className="col-souple">
                          {f.ressource_nom ?? (
                            <span className="sourdine">toute la société</span>
                          )}
                        </td>
                        <td className="code">{f.du}</td>
                        <td className="code">{f.au ?? <Absent />}</td>
                        <td className="actions-liste">
                          {f.au === null ? (
                            <button
                              className="lien-action"
                              type="button"
                              disabled={p.envoi}
                              onClick={() => {
                                p.quandFermerFrais(f.id);
                              }}
                            >
                              Arrêter
                            </button>
                          ) : (
                            <button
                              className="lien-action risque"
                              type="button"
                              disabled={p.envoi}
                              onClick={() => {
                                p.quandSupprimerFrais(f.id);
                              }}
                            >
                              Supprimer
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {p.onglet === 'traction' && (
          <>
            <div className="barre-outils">
              <button className="bouton" type="button" onClick={p.quandOuvrirContrat}>
                Ajouter un contrat
              </button>
            </div>
            <p className="aide-saisie">
              Un tractionnaire est un véhicule ou un chauffeur d’une autre
              société, exploité par vous et inscrit sur votre planning comme les
              vôtres. Son coût est CONNU — au jour, au kilomètre ou au voyage —
              et ne se calcule pas d’un prix mensuel.
            </p>

            {p.nouveauContrat !== null && (
              <BoiteContrat
                saisie={p.nouveauContrat}
                transporteurs={p.transporteurs}
                vehicules={p.vehicules}
                chauffeurs={p.chauffeurs}
                envoi={p.envoi}
                refus={p.refus}
                quandChamp={p.quandChampContrat}
                quandFermer={p.quandFermerBoite}
                quandEnregistrer={p.quandEnregistrerContrat}
              />
            )}

            {p.contrats.length === 0 ? (
              <div className="vide">
                <p>Aucun contrat de traction.</p>
              </div>
            ) : (
              <div className="tableau-cadre">
                <table>
                  <thead>
                    <tr>
                      <th scope="col" className="col-souple">
                        Transporteur
                      </th>
                      <th scope="col">Véhicule</th>
                      <th scope="col">Chauffeur</th>
                      <th scope="col">Mode</th>
                      <th scope="col" className="prix">
                        Prix
                      </th>
                      <th scope="col">Depuis</th>
                      <th scope="col" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {p.contrats.map((c) => (
                      <tr key={c.id}>
                        <td className="col-souple">{c.transporteur}</td>
                        <td className="code">{c.vehicule ?? <Absent />}</td>
                        <td>{c.chauffeur ?? <Absent />}</td>
                        <td className="sourdine">
                          {c.mode === 'jour'
                            ? 'au jour'
                            : c.mode === 'km'
                              ? 'au kilomètre'
                              : 'au voyage'}
                        </td>
                        <td className="prix">
                          {euros(Number(c.prix), c.mode === 'km' ? 4 : 2) ?? <Absent />}
                        </td>
                        <td className="code">{c.du}</td>
                        <td className="actions-liste">
                          <button
                            className="lien-action risque"
                            type="button"
                            disabled={p.envoi}
                            onClick={() => {
                              p.quandSupprimerContrat(c.id);
                            }}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/**
 * Combien de jours du mois tombent sur les cases cochées.
 *
 * CALCULÉ ICI, et c'est la seule exception à la règle « on ne recompte pas ce
 * que la base compte » : la boîte doit montrer l'effet d'une case AVANT
 * d'enregistrer, et la base ne connaît pas encore la saisie en cours. Le
 * chiffre affiché dans le TABLEAU, lui, vient toujours de l'API.
 */
function joursDansLeMois(mois: string, jours: Readonly<Record<CleJour, boolean>>): number {
  const morceaux = /^(\d{4})-(\d{2})$/.exec(mois);
  if (morceaux === null) return 0;
  const annee = Number(morceaux[1]);
  const rangMois = Number(morceaux[2]) - 1;
  let compte = 0;
  for (let jour = 1; jour <= 31; jour += 1) {
    const d = new Date(Date.UTC(annee, rangMois, jour));
    if (d.getUTCMonth() !== rangMois) break;
    // `getUTCDay()` : 0 = dimanche. SEMAINE commence au lundi.
    const rang = (d.getUTCDay() + 6) % 7;
    const cle = SEMAINE[rang]?.cle;
    if (cle !== undefined && jours[cle]) compte += 1;
  }
  return compte;
}

function BoiteFrais({
  saisie,
  ressources,
  envoi,
  refus,
  quandChamp,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: {
    readonly libelle: string;
    readonly montant: string;
    readonly unite: UniteFrais;
    readonly ressource: string;
  };
  readonly ressources: readonly RessourceFinance[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: string, v: string) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  const pret = saisie.libelle.trim() !== '' && saisie.montant.trim() !== '';
  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Ajouter un frais</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="frais-libelle">
            Libellé
          </label>
          <input
            id="frais-libelle"
            className="champ-boite"
            type="text"
            value={saisie.libelle}
            disabled={envoi}
            onChange={(e) => quandChamp('libelle', e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="frais-montant">
            Montant
          </label>
          <input
            id="frais-montant"
            className="champ-boite"
            type="text"
            inputMode="decimal"
            value={saisie.montant}
            disabled={envoi}
            onChange={(e) => quandChamp('montant', e.target.value)}
          />

          <label className="etiquette-boite" htmlFor="frais-unite">
            Unité
          </label>
          <select
            id="frais-unite"
            className="champ-boite"
            value={saisie.unite}
            disabled={envoi}
            onChange={(e) => quandChamp('unite', e.target.value)}
          >
            <option value="mois">par mois</option>
            <option value="km">au kilomètre</option>
            <option value="heure">à l’heure</option>
          </select>
          {saisie.unite === 'km' && (
            <p className="aide-boite">
              Quatre décimales se saisissent : « 0,0320 » de pneumatiques
              arrondi à 0,03 se tromperait de 6 %.
            </p>
          )}

          <label className="etiquette-boite" htmlFor="frais-ressource">
            Sur quoi
          </label>
          <select
            id="frais-ressource"
            className="champ-boite"
            value={saisie.ressource}
            disabled={envoi}
            onChange={(e) => quandChamp('ressource', e.target.value)}
          >
            <option value="">Toute la société</option>
            {ressources
              .filter((r) => r.actif)
              .map((r) => (
                <option key={`${r.sorte}-${r.id}`} value={`${r.sorte}:${r.id}`}>
                  {sorteDite(r.sorte)} · {r.nom}
                </option>
              ))}
          </select>

          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button className="bouton" type="submit" disabled={envoi || !pret}>
              {envoi ? 'Enregistrement…' : 'Ajouter'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BoiteContrat({
  saisie,
  transporteurs,
  vehicules,
  chauffeurs,
  envoi,
  refus,
  quandChamp,
  quandFermer,
  quandEnregistrer,
}: {
  readonly saisie: {
    readonly transporteurId: string;
    readonly vehiculeId: string;
    readonly chauffeurId: string;
    readonly mode: 'jour' | 'km' | 'voyage';
    readonly prix: string;
  };
  readonly transporteurs: readonly LigneListe[];
  /**
   * LE PARC, réduit à ce que la boîte de contrat affiche. `lireParc` rend des
   * lignes ouvertes — chaque parc a ses colonnes — et n'en garder que deux
   * évite de dépendre ici de la forme d'un camion.
   */
  readonly vehicules: readonly LigneRessource[];
  readonly chauffeurs: readonly LigneRessource[];
  readonly envoi: boolean;
  readonly refus: string | null;
  readonly quandChamp: (cle: string, v: string) => void;
  readonly quandFermer: () => void;
  readonly quandEnregistrer: () => void;
}) {
  const pret =
    saisie.transporteurId !== '' &&
    saisie.prix.trim() !== '' &&
    (saisie.vehiculeId !== '' || saisie.chauffeurId !== '');

  return (
    <div className="voile-boite" role="presentation">
      <div className="boite-decrochage" role="dialog" aria-modal="true">
        <h3>Contrat de traction</h3>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          <label className="etiquette-boite" htmlFor="contrat-transporteur">
            Chez qui
          </label>
          <select
            id="contrat-transporteur"
            className="champ-boite"
            value={saisie.transporteurId}
            disabled={envoi}
            onChange={(e) => quandChamp('transporteurId', e.target.value)}
          >
            <option value="">Choisir…</option>
            {transporteurs
              .filter((t) => t.actif)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nom}
                </option>
              ))}
          </select>

          <label className="etiquette-boite" htmlFor="contrat-vehicule">
            Véhicule
          </label>
          <select
            id="contrat-vehicule"
            className="champ-boite"
            value={saisie.vehiculeId}
            disabled={envoi}
            onChange={(e) => quandChamp('vehiculeId', e.target.value)}
          >
            <option value="">Aucun</option>
            {vehicules.map((v) => (
              <option key={v.id} value={v.id}>
                {v.nom}
              </option>
            ))}
          </select>

          <label className="etiquette-boite" htmlFor="contrat-chauffeur">
            Chauffeur
          </label>
          <select
            id="contrat-chauffeur"
            className="champ-boite"
            value={saisie.chauffeurId}
            disabled={envoi}
            onChange={(e) => quandChamp('chauffeurId', e.target.value)}
          >
            <option value="">Aucun</option>
            {chauffeurs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nom}
              </option>
            ))}
          </select>
          <p className="aide-boite">
            Au moins l’un des deux : un camion seul (on met notre chauffeur
            dedans) ou un chauffeur seul (il conduit nos camions).
          </p>

          <label className="etiquette-boite" htmlFor="contrat-mode">
            Rémunération
          </label>
          <select
            id="contrat-mode"
            className="champ-boite"
            value={saisie.mode}
            disabled={envoi}
            onChange={(e) => quandChamp('mode', e.target.value)}
          >
            <option value="jour">forfait jour</option>
            <option value="km">forfait kilomètre</option>
            <option value="voyage">forfait voyage</option>
          </select>

          <label className="etiquette-boite" htmlFor="contrat-prix">
            Prix
          </label>
          <input
            id="contrat-prix"
            className="champ-boite"
            type="text"
            inputMode="decimal"
            value={saisie.prix}
            disabled={envoi}
            onChange={(e) => quandChamp('prix', e.target.value)}
          />

          {refus !== null && <p className="bandeau-refus">{refus}</p>}

          <div className="actions">
            <button className="bouton" type="submit" disabled={envoi || !pret}>
              {envoi ? 'Enregistrement…' : 'Ajouter'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandFermer}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'écran
// ---------------------------------------------------------------------------

const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette ligne n’existe plus. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce nom est déjà pris.',
  invalide: 'Vérifiez ce qui a été saisi.',
  conflit: 'Cette ressource a déjà un coût ouvert. Rechargez l’écran.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Des lignes s’y rattachent encore.',
  ordre_annule: 'Cet ordre est annulé.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee: 'Une étape déjà validée ne se déplace plus.',
  jour_sans_etape: 'Aucune étape ce jour-là.',
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  litige_deja_ouvert: 'Cette étape a déjà un litige ouvert.',
  litige_deja_resolu: 'Ce litige est déjà résolu.',
};

export interface ProprietesFinance {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

export function EcranFinance({ jeton, quandAccesPerdu }: ProprietesFinance) {
  const [onglet, setOnglet] = useState<OngletFinance>('ressources');
  const [mois, setMois] = useState(() => moisCourant());
  const [etat, setEtat] = useState<EtatFinance>({ nom: 'chargement' });
  const [frais, setFrais] = useState<readonly FraisFinance[]>([]);
  const [contrats, setContrats] = useState<readonly ContratTraction[]>([]);
  const [gazole, setGazole] = useState<readonly IndiceGazole[]>([]);
  const [transporteurs, setTransporteurs] = useState<readonly LigneListe[]>([]);
  const [vehicules, setVehicules] = useState<readonly LigneRessource[]>([]);
  const [chauffeurs, setChauffeurs] = useState<readonly LigneRessource[]>([]);
  const [cout, setCout] = useState<SaisieCout | null>(null);
  const [amplitudeSaisie, setAmplitudeSaisie] = useState('');
  const [nouveauFrais, setNouveauFrais] = useState<ProprietesVueFinance['nouveauFrais']>(
    null,
  );
  const [nouveauContrat, setNouveauContrat] =
    useState<ProprietesVueFinance['nouveauContrat']>(null);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  const relire = useCallback(async () => {
    const j = await avecJeton();
    if (j === null) return;
    const lu = await lireRessourcesFinance(j, mois);
    if (lu.etat === 'ok') {
      setEtat({ nom: 'prete', ...lu.valeur });
      setAmplitudeSaisie(lu.valeur.reglages.amplitude_heures ?? '');
    } else {
      if (lu.cause === 'acces') quandAccesPerdu();
      setEtat({ nom: 'panne' });
    }
  }, [avecJeton, mois, quandAccesPerdu]);

  useEffect(() => {
    let abandonne = false;
    setEtat({ nom: 'chargement' });
    void (async () => {
      if (!abandonne) await relire();
    })();
    return () => {
      abandonne = true;
    };
  }, [relire]);

  /** Les référentiels, chargés une fois : ils ne bougent pas d'un mois à l'autre. */
  useEffect(() => {
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const [f, c, g, t, v, ch] = await Promise.all([
        lireFrais(j),
        lireContrats(j),
        lireGazole(j),
        lireListe(j, ANNUAIRE_TRANSPORTEURS),
        lireParc(j, PARC_VEHICULES),
        lireParc(j, PARC_CHAUFFEURS),
      ]);
      if (abandonne) return;
      if (f.etat === 'ok') setFrais(f.valeur);
      if (c.etat === 'ok') setContrats(c.valeur);
      if (g.etat === 'ok') setGazole(g.valeur);
      if (t.etat === 'ok') setTransporteurs(t.valeur);
      // Le nom d'un camion est sa plaque, celui d'un chauffeur son nom. On ne
      // garde que l'identifiant et le mot affiché.
      if (v.etat === 'ok') {
        setVehicules(
          v.valeur.map((l) => ({ id: l.id, nom: String(l['immatriculation'] ?? l.id) })),
        );
      }
      if (ch.etat === 'ok') {
        setChauffeurs(ch.valeur.map((l) => ({ id: l.id, nom: String(l['nom'] ?? l.id) })));
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton]);

  const finir = (resultat: { etat: 'ok' | 'echec'; cause?: EchecApi }) => {
    setEnvoi(false);
    setRefus(
      resultat.etat === 'ok' || resultat.cause === undefined
        ? null
        : REFUS[resultat.cause],
    );
  };

  return (
    <VueFinance
      onglet={onglet}
      etat={etat}
      mois={mois}
      frais={frais}
      contrats={contrats}
      gazole={gazole}
      transporteurs={transporteurs}
      vehicules={vehicules}
      chauffeurs={chauffeurs}
      cout={cout}
      amplitudeSaisie={amplitudeSaisie}
      nouveauFrais={nouveauFrais}
      nouveauContrat={nouveauContrat}
      envoi={envoi}
      refus={refus}
      quandOnglet={setOnglet}
      quandMois={setMois}
      quandAmplitude={setAmplitudeSaisie}
      quandEnregistrerAmplitude={() => {
        const propre = amplitudeSaisie.trim().replace(',', '.');
        const actuelle =
          etat.nom === 'prete' ? (etat.reglages.amplitude_heures ?? '') : '';
        if (propre === actuelle) return;
        setEnvoi(true);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await reglerLaFinance(j, {
            amplitude_heures: propre === '' ? null : propre,
          });
          finir(fait);
          if (fait.etat === 'ok') await relire();
        })();
      }}
      quandNature={(r) => {
        setEnvoi(true);
        setRefus(null);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await changerLaNature(
            j,
            r.sorte,
            r.id,
            r.nature === 'propre' ? 'tractionnaire' : 'propre',
          );
          finir(fait);
          if (fait.etat === 'ok') await relire();
        })();
      }}
      quandCout={(r) => {
        setRefus(null);
        setCout({
          sorte: r.sorte,
          id: r.id,
          nom: r.nom,
          prixMensuel: r.prix_mensuel ?? '',
          consommation: r.consommation ?? '',
          du: `${mois}-01`,
          jours:
            r.lundi === null
              ? OUVRES
              : (Object.fromEntries(
                  SEMAINE.map((jour) => [jour.cle, r[jour.cle] === true]),
                ) as Record<CleJour, boolean>),
        });
      }}
      quandChampCout={(cle, v) => {
        setCout((a) => (a === null ? a : { ...a, [cle]: v }));
      }}
      quandJourCout={(jour) => {
        setCout((a) =>
          a === null ? a : { ...a, jours: { ...a.jours, [jour]: !a.jours[jour] } },
        );
      }}
      quandEnregistrerCout={() => {
        if (cout === null || refusCout(cout) !== null) return;
        setEnvoi(true);
        setRefus(null);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await poserUnCout(j, corpsCout(cout));
          finir(fait);
          if (fait.etat === 'ok') {
            setCout(null);
            await relire();
          }
        })();
      }}
      quandSupprimerCout={(id) => {
        setEnvoi(true);
        setRefus(null);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await supprimerUnCout(j, id);
          finir(fait);
          if (fait.etat === 'ok') await relire();
        })();
      }}
      quandFermerBoite={() => {
        setCout(null);
        setNouveauFrais(null);
        setNouveauContrat(null);
        setRefus(null);
      }}
      quandOuvrirFrais={() => {
        setRefus(null);
        setNouveauFrais({ libelle: '', montant: '', unite: 'mois', ressource: '' });
      }}
      quandChampFrais={(cle, v) => {
        setNouveauFrais((a) => (a === null ? a : { ...a, [cle]: v }));
      }}
      quandEnregistrerFrais={() => {
        const saisie = nouveauFrais;
        if (saisie === null) return;
        setEnvoi(true);
        setRefus(null);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const [sorte, id] = saisie.ressource.split(':');
          const colonne =
            sorte === 'vehicule'
              ? 'vehicule_id'
              : sorte === 'remorque'
                ? 'remorque_id'
                : sorte === 'chauffeur'
                  ? 'chauffeur_id'
                  : null;
          const fait = await creerUnFrais(j, {
            libelle: saisie.libelle.trim(),
            montant: saisie.montant.trim().replace(',', '.'),
            unite: saisie.unite,
            ...(colonne === null || id === undefined ? {} : { [colonne]: id }),
          });
          finir(fait);
          if (fait.etat === 'ok') {
            setNouveauFrais(null);
            const relus = await lireFrais(j);
            if (relus.etat === 'ok') setFrais(relus.valeur);
            await relire();
          }
        })();
      }}
      quandFermerFrais={(id) => {
        setEnvoi(true);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await fermerUnFrais(j, id, new Date().toISOString().slice(0, 10));
          finir(fait);
          if (fait.etat === 'ok') {
            const relus = await lireFrais(j);
            if (relus.etat === 'ok') setFrais(relus.valeur);
            await relire();
          }
        })();
      }}
      quandSupprimerFrais={(id) => {
        setEnvoi(true);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await supprimerUnFrais(j, id);
          finir(fait);
          if (fait.etat === 'ok') {
            const relus = await lireFrais(j);
            if (relus.etat === 'ok') setFrais(relus.valeur);
            await relire();
          }
        })();
      }}
      quandOuvrirContrat={() => {
        setRefus(null);
        setNouveauContrat({
          transporteurId: '',
          vehiculeId: '',
          chauffeurId: '',
          mode: 'jour',
          prix: '',
        });
      }}
      quandChampContrat={(cle, v) => {
        setNouveauContrat((a) => (a === null ? a : { ...a, [cle]: v }));
      }}
      quandEnregistrerContrat={() => {
        const saisie = nouveauContrat;
        if (saisie === null) return;
        setEnvoi(true);
        setRefus(null);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await creerUnContrat(j, {
            transporteur_id: saisie.transporteurId,
            mode: saisie.mode,
            prix: saisie.prix.trim().replace(',', '.'),
            ...(saisie.vehiculeId === '' ? {} : { vehicule_id: saisie.vehiculeId }),
            ...(saisie.chauffeurId === '' ? {} : { chauffeur_id: saisie.chauffeurId }),
          });
          finir(fait);
          if (fait.etat === 'ok') {
            setNouveauContrat(null);
            const relus = await lireContrats(j);
            if (relus.etat === 'ok') setContrats(relus.valeur);
          }
        })();
      }}
      quandSupprimerContrat={(id) => {
        setEnvoi(true);
        void (async () => {
          const j = await avecJeton();
          if (j === null) return;
          const fait = await supprimerUnContrat(j, id);
          finir(fait);
          if (fait.etat === 'ok') {
            const relus = await lireContrats(j);
            if (relus.etat === 'ok') setContrats(relus.valeur);
          }
        })();
      }}
    />
  );
}
